<template>
    <loading-icon class="c-card" v-if="loading && !card.title"></loading-icon>
    <section class="c-card c-card--report" v-else>

    <div class="c-card__header">
        <h2 class="c-card__title t-headline" v-if="card.title">{{ card.title }}</h2>
    </div>

    <div class="c-card__body u-position-relative">
      <p class="u-small-paragraph" v-if="!dataAvailable">
        {{ $t('web.card_report.data_not_available') }}
      </p>

      <button
        v-if  = "dataAvailable && card.download_enabled && !downloading" @click="downloadReport"
        class = "c-button c-button--dataTables-custom-download-button t-button-secondary"
      >
        {{ card.download_button_text }}
      </button>
      <button
        v-if = "downloading"
        class = "c-button c-button--dataTables-custom-download-button download-button-loading t-button-secondary">
        <i class="fa fa-spinner fa-spin fa-1x t-loading-icon"></i>
        <span>{{this.$t(loadingMessage)}}</span>
      </button>

      <div v-show="dataAvailable">
        <table ref="dtable" style="min-width:100%">
        </table>
      </div>
    </div>

    </section><!--END CARD-->
</template>


<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { isNil, filter, map, find, kebabCase, zipObject, maxBy, uniq } from 'lodash'
import $ from 'jquery'
import 'datatables.net'
import 'moment'
import 'datatables.net-plugins/sorting/datetime-moment'
import util from '@/functions'
import json2csv from 'json2csv'

export default {
  props: ['card'],
  data () {
    return {
      dtable: null,
      rows: [],
      groupRows: [],
      params: null,
      columnDefs: [],
      downloading: false,
      interval: null,
      loadingMessage: 'utils.datatables.processing'
    }
  },
  computed: {
    ...mapGetters({
      reportData: 'reportData',
      websiteConfig: 'websiteConfig'
    }),
    loading () {
      return !this.card.cardLoaded
    },
    dataAvailable () {
      return !(isNil(this.card.report_id) || isNil(this.reportData[this.card.report_id]))
    },
    datatablesLanguage () {
      return {
        'emptyTable': this.$t('utils.datatables.empty_table'),
        'info': this.$t('utils.datatables.records_info'),
        'infoEmpty': this.$t('utils.datatables.showing_zero_records'),
        'infoFiltered': this.$t('utils.datatables.info_filtered'),
        'infoPostFix': this.$t('utils.datatables.append_to_info'),
        'thousands': this.$t('utils.datatables.info_thousands_separator'),
        'lengthMenu': this.$t('utils.datatables.records_per_page'),
        'loadingRecords': this.$t('utils.datatables.loading'),
        'processing': this.$t('utils.datatables.processing'),
        'search': this.$t('utils.datatables.search'),
        'zeroRecords': this.$t('utils.datatables.no_records_found'),
        'paginate': {
          'first': this.$t('utils.datatables.page_first'),
          'last': this.$t('utils.datatables.page_last'),
          'next': this.$t('utils.datatables.page_next'),
          'previous': this.$t('utils.datatables.page_previous')
        },
        'aria': {
          'sortAscending': this.$t('utils.datatables.sort_ascending'),
          'sortDescending': this.$t('utils.datatables.sort_descending')
        }
      }
    },
    hasGroupFields () {
      return filter(this.reportData[this.card.report_id], (item) => {
        return !isNil(item[0].group)
      }).length > 0
    }
  },
  mounted () {
    $.fn.dataTable.moment('MMM DD, YYYY')
    $.fn.dataTable.moment('MMM DD, YYYY hh:mm a')
    this.renderDataTable()
  },
  methods: {
    ...mapActions([
      'LOAD_CARD'
    ]),
    ...mapMutations(['UPDATE_REPORT_DATA']),
    /**
     * Iterate between two group indexes from higher to lower and
     * return the group rows contained between them with the first group row
     * found being returned for each level, including the groups from the arguments
     * Example: getGroupRows(3, 1) would return group rows for groups 1,2 and 3
     *
     * @param startGroup number
     * @param endGroup number
     * @return array
     */
    getGroupRows (startGroup, endGroup) {
      let rows = []
      let foundLevels = {}
      // convert the group indexes to row indexes just after the group rows data
      let startIndex = find(this.rows, (item) => { return item[0].groupIndex === startGroup })[0].originalIndex
      let endIndex = find(this.rows, (item) => { return item[0].groupIndex === endGroup })
      endIndex = endIndex ? endIndex[0].originalIndex : 0

      for (var i = startIndex; i >= endIndex; i--) {
        let groupRow = find(this.groupRows, (item) => {
          return item[0].originalIndex === i
        })
        if (groupRow && isNil(foundLevels[groupRow[0].level])) {
          rows.unshift(groupRow)
          foundLevels[groupRow[0].level] = true
        }
      }
      return rows
    },
    renderDataTable () {
      if (!this.dataAvailable) {
        return
      }

      if (this.dtable) {
          // destroy the existing datatable in case that the new report has changes
          // to the columns and not just the data
        this.dtable.destroy()
      }

      this.initializeData()

      var self = this
      let token = localStorage.getItem('token_' + this.websiteConfig.id)
      let dataTableSettings = {
        language: this.datatablesLanguage,
        dom: 'lfr<"dataTables_top_pagination"p>tip',
        columnDefs: this.columnDefs,
        searching: false,
        processing: true,
        serverSide: true,
        aaSorting: [],
        ajax: {
          type: 'GET',
          url: process.env.API_URL + '/api/platform/v1/cards/' + this.card.id,
          headers: {'Authorization': 'Bearer ' + token},
          dataFilter: function (data) {
            let json = JSON.parse(data)

            self.UPDATE_REPORT_DATA(json)
            self.initializeData()

            json.recordsTotal = json.recordsTotal
            json.recordsFiltered = json.recordsFiltered
            json.data = self.rows

            return JSON.stringify(json)
          }
        },
        drawCallback: function (settings) {
            // when redrawing the table insert group rows where needed
          if (self.groupRows.length > 0) {
            let shownGroups = []
            var api = this.api()
            var rowsTR = api.rows({page: 'current'}).nodes()
            var rowsData = api.rows({page: 'current'}).data()
              // insert group rows before each data row which should have group rows
            for (let displayIndex = 0; displayIndex < rowsData.length; displayIndex++) {
              let groupRows = []
              if (displayIndex === 0) {
                  // insert group rows before the first row on the page
                  // search for group rows between the current group index up to index 0
                groupRows = self.getGroupRows(
                    rowsData[displayIndex][0].groupIndex,
                    0
                  )
                shownGroups.push(rowsData[displayIndex][0].groupIndex)
              } else {
                if (shownGroups.indexOf(rowsData[displayIndex][0].groupIndex) === -1) {
                    // search for group rows between the current group and the previous one
                  groupRows = self.getGroupRows(
                      rowsData[displayIndex][0].groupIndex,
                      rowsData[displayIndex][0].groupIndex - 1
                    )
                  shownGroups.push(rowsData[displayIndex][0].groupIndex)
                }
              }
              if (groupRows.length) {
                for (let i = 0; i < groupRows.length; i++) {
                  let level = groupRows[i][0].level
                  let html = '<tr class="row-group row-group-' + level + '"><td colspan="' +
                      (rowsData[displayIndex].length - 1) + '">' + groupRows[i][1] + '</td></tr>'
                  $(rowsTR).eq(displayIndex).before(html)
                }
              }
            }
          }
        },
        scrollX: true
      }
      if (this.hasGroupFields) {
        dataTableSettings.orderFixed = [0, 'asc']
      }
      this.dtable = $(this.$refs.dtable).DataTable(dataTableSettings)

      this.dtable.on('xhr', function () {
        self.params = self.dtable.ajax.params()
      })
    },
    initializeData () {
      this.columnDefs = [{
        name: 'ids',
        orderable: false,
        searchable: false,
        visible: false,
        targets: 0
      }]

      let maxGroupLevel = 0
      let groupIndex = 1

      if (this.hasGroupFields) {
        this.columnDefs[0]['render'] = {
          _: 'groupIndex',
          sort: 'groupIndex'
        }
        maxGroupLevel = maxBy(this.reportData[this.card.report_id], (item) => { return item[0].level })[0].level
      }

      let rows = map(
        this.reportData[this.card.report_id],
        (item, index) => {
          let newItem = item
          newItem[0].originalIndex = index
          if (this.hasGroupFields) {
            // for each data/group row set the group index (starting from it) it belongs to
            newItem[0].groupIndex = newItem[0].level ? groupIndex : groupIndex - 1
            if (newItem[0].level === maxGroupLevel) {
              // after adding group row with maximal nesting level the next row belongs to the next group
              groupIndex++
            }
          }
          return newItem
        }
      )

      let groupFields = uniq(map(
          filter(rows, (item) => {
            return !isNil(item[0].group)
          }), (item) => { return item[0].group }
        ))

      for (var i = 0; i < this.card.headers.length; i++) {
        this.columnDefs.push({
          title: this.card.headers[i],
          targets: i + 1,
          orderable: (groupFields.indexOf(this.card.fields[i + 1].name) === -1)
        })
      }

      this.rows = filter(
        rows, (item) => {
          return isNil(item[0].group)
        }
      )
      this.groupRows = filter(
        rows, (item) => {
          return !isNil(item[0].group)
        }
      )
    },
    /**
     * Generate a csv file from report data with filename in 'card-title--yyyy-mm-dd.csv' format and trigger a download
     * @return null
     */
    downloadReport () {
      let exportParams = this.params
      exportParams['export'] = true

      this.downloading = true
      this.interval = setInterval(this.updateLoadingMessage, 10000)

      this.$store.dispatch('LOAD_CARD', {id: this.card.id, query: $.param(exportParams)})
       .then(() => {
         let filename = kebabCase(this.card.title) + '--' + new Date().toISOString().slice(0, 10) + '.csv'
         let csv = this.convertToCSV(this.card.headers, this.reportData[this.card.report_id])
         util.triggerFileDownload(csv, filename, 'text/csv;charset=utf-8;', true)
         this.downloading = false
         clearInterval(this.interval)
       })
       .catch(error => {
         console.log(error)
         this.downloading = false
         clearInterval(this.interval)
       })
    },
    /**
     * Takes the header row and an array of report rows and returns the CSV data as a string
     * @param headerRow array
     * @param records array
     * @return string
     */
    convertToCSV (headerRow, records) {
      let rows = [headerRow].concat(
        map(records, (item) => { return item.slice(1) })
      )
      let keys = map(headerRow, kebabCase)
      // convert the rows arrays to objects with object keys coming from the keys array matching on array index
      let csv = map(rows, (row) => { return zipObject(keys, row) })
      return json2csv.parse(csv, { fields: keys, header: false })
    },
    updateLoadingMessage () {
      this.loadingMessage = this.loadingMessage === 'utils.datatables.processing'
        ? 'utils.datatables.please_wait'
        : 'utils.datatables.processing'
    }
  },
  watch: {
    /**
     * Simple forms (not inside multi cards) don't have all the data loaded
     * at the time of mounted()
     */
    dataAvailable () {
      if (this.dataAvailable) {
        this.$nextTick(() => {
          this.renderDataTable()
        })
      }
    }
  }
}
</script>

<style>
.dataTables_processing {
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// Report.vue?0adc9222